// src/services/AuthService.js
import { CognitoUser, CognitoRefreshToken } from "amazon-cognito-identity-js";

class AuthService {
  constructor(userPool) {
    this.userPool = userPool;
  }

  /**
   * Refresh the session using the refresh token
   * @returns {Promise<Object>} Object with new tokens
   */
  refreshSession = async () => {
    try {
      const currentUser = this.userPool.getCurrentUser();

      if (!currentUser) {
        throw new Error("No current user found");
      }

      const refreshToken = localStorage.getItem("refreshToken");

      if (!refreshToken) {
        throw new Error("No refresh token found");
      }

      const cognitoRefreshToken = new CognitoRefreshToken({
        RefreshToken: refreshToken,
      });

      return new Promise((resolve, reject) => {
        currentUser.refreshSession(cognitoRefreshToken, (err, session) => {
          if (err) {
            console.error("Error refreshing session:", err);
            reject(err);
            return;
          }

          // Store new tokens
          const accessToken = session.getAccessToken().getJwtToken();
          const idToken = session.getIdToken().getJwtToken();
          const newRefreshToken = session.getRefreshToken().getToken();

          localStorage.setItem("accessToken", accessToken);
          localStorage.setItem("idToken", idToken);
          localStorage.setItem("refreshToken", newRefreshToken);

          console.log("Session refreshed successfully");
          resolve({
            accessToken,
            idToken,
            refreshToken: newRefreshToken,
            session,
          });
        });
      });
    } catch (error) {
      console.error("Failed to refresh session:", error);
      // Clear tokens if refresh fails
      localStorage.removeItem("accessToken");
      localStorage.removeItem("idToken");
      localStorage.removeItem("refreshToken");
      throw error;
    }
  };

  /**
   * Request a password reset code
   * @param {string} email - User's email
   * @returns {Promise<void>}
   */
  forgotPassword = (email) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: this.userPool,
    });

    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: (data) => resolve(data),
        onFailure: (err) => reject(err),
      });
    });
  };

  /**
   * Confirm password reset with code
   * @param {string} email - User's email
   * @param {string} code - Verification code
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   */
  confirmPassword = (email, code, newPassword) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: this.userPool,
    });

    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
      });
    });
  };

  /**
   * Check if the token is expired
   * @param {string} token - JWT token
   * @returns {boolean} True if token is expired
   */
  isTokenExpired = (token) => {
    try {
      if (!token) return true;

      // Decode the token
      const payload = JSON.parse(atob(token.split(".")[1]));

      if (!payload.exp) return true;

      // Get current time in seconds
      const currentTime = Math.floor(Date.now() / 1000);

      // Check if token is expired
      // Add 5 minute buffer to refresh before actual expiration
      return payload.exp - currentTime < 300; // 5 minutes in seconds
    } catch (error) {
      console.error("Error checking token expiration:", error);
      return true;
    }
  };
}

export default AuthService;
